/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useCallback, useMemo, useState, use, useEffect } from 'react';
import { useLocation, useNavigation, useRouteLoaderData } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch, ShopperProducts } from '@/scapi';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@/components/typography';
import { UITarget } from '@/targets/ui-target';
import type { FilterValue, RefinementProps } from './types';
import RefineDefault from './refine-default';
import RefineColor from './refine-color';
import RefineSize from './refine-size';
import RefinePrice from './refine-price';
// @sfdc-extension-line SFDC_EXT_BOPIS
import RefineInventory from '@/extensions/bopis/components/refine-inventory';
import QuickFilters from '@/components/quick-filters';
import { Checkbox } from '@/components/ui/checkbox';
import { FiltersIcon } from '@/components/icons';

const FILTER_PANEL_SKIN = [
    // Generic checkbox (shadcn Checkbox → button[data-slot="checkbox"])
    '[&_[data-slot=checkbox]]:size-6',
    '[&_[data-slot=checkbox]]:shrink-0',
    '[&_[data-slot=checkbox]]:rounded-[4px]',
    '[&_[data-slot=checkbox]]:border',
    '[&_[data-slot=checkbox]]:border-[#555555]',
    '[&_[data-slot=checkbox]]:bg-black/[0.08]',
    '[&_[data-slot=checkbox][data-state=checked]]:border-black',
    // Color swatch checkbox (RefineColor renders button.color-checkbox with --checkbox-color set inline)
    '[&_button.color-checkbox]:size-6',
    '[&_button.color-checkbox]:shrink-0',
    '[&_button.color-checkbox]:rounded-[4px]',
    '[&_button.color-checkbox[data-state=checked]]:border-black',
    '[&_button.color-checkbox[data-state=checked]]:shadow-[0_0_0_1px_#000000]',
    // Generic filter-value row (Color / Size / etc. label rows)
    '[&_label]:flex',
    '[&_label]:items-center',
    '[&_label]:gap-[19px]',
    '[&_label]:cursor-pointer',
    '[&_label]:border-b',
    '[&_label]:border-black/10',
    '[&_label]:pb-2.5',
    '[&_label]:pt-4',
    '[&_label:last-child]:border-b-0',
    '[&_label_span]:text-sm',
    '[&_label_span]:font-normal',
    '[&_label_span]:leading-5',
    '[&_label_span]:tracking-normal',
    '[&_label_span]:text-black',
    // Category tree rows override the generic row: no divider, tighter/looser spacing
    '[&_label.cat]:border-b-0',
    '[&_label.cat]:gap-3',
    '[&_label.cat]:py-2',
    '[&_label.cat_[data-slot=checkbox]]:size-[18px]',
    '[&_label.cat_[data-slot=checkbox]]:rounded-[4px]',
    // Price slider (RefinePrice)
    '[&_.price-slider-track]:h-1',
    '[&_.price-slider-track]:rounded-full',
    '[&_.price-slider-track]:bg-black',
    '[&_.price-slider-container_input[type=range]]:h-1',
    '[&_.price-slider-container_input[type=range]]:appearance-none',
    '[&_.price-slider-container_input[type=range]]:bg-transparent',
    '[&_.price-slider-container_input[type=range]::-webkit-slider-thumb]:appearance-none',
    '[&_.price-slider-container_input[type=range]::-webkit-slider-thumb]:size-[18px]',
    '[&_.price-slider-container_input[type=range]::-webkit-slider-thumb]:rounded-full',
    '[&_.price-slider-container_input[type=range]::-webkit-slider-thumb]:bg-black',
    '[&_.price-slider-container_input[type=range]::-moz-range-thumb]:size-[18px]',
    '[&_.price-slider-container_input[type=range]::-moz-range-thumb]:rounded-full',
    '[&_.price-slider-container_input[type=range]::-moz-range-thumb]:border-0',
    '[&_.price-slider-container_input[type=range]::-moz-range-thumb]:bg-black',
].join(' ');

export default function CategoryRefinements({
    result,
    refine = [],
    category,
    onToggle,
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
    refine: string[];
    category?: ShopperProducts.schemas['Category'];
    onToggle?: () => void;
}): ReactElement {
    const { t } = useTranslation();
    const appLoaderData = useRouteLoaderData('routes/_app') as {
        root?: Promise<ShopperProducts.schemas['Category']>;
        subs?: Promise<ShopperProducts.schemas['Category'][]>;
    } | undefined;
    const rootCategory = appLoaderData?.root ? use(appLoaderData.root) : undefined;
    const [subCategories, setSubCategories] = useState<ShopperProducts.schemas['Category'][]>(() => {
        const maybeSubs = appLoaderData?.subs as any;
        if (Array.isArray(maybeSubs)) return maybeSubs;
        if (maybeSubs && typeof maybeSubs === 'object' && 'value' in maybeSubs && Array.isArray(maybeSubs.value)) {
            return maybeSubs.value;
        }
        return [];
    });

    useEffect(() => {
        let isMounted = true;
        if (appLoaderData?.subs && typeof (appLoaderData.subs as any).then === 'function') {
            void appLoaderData.subs.then((cats) => {
                if (isMounted && Array.isArray(cats)) {
                    setSubCategories(cats);
                }
            });
        }
        return () => {
            isMounted = false;
        };
    }, [appLoaderData?.subs]);

    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';

    const [selectedSection, setSelectedSection] = useState<string>('category');
    const [initialRefines] = useState<string[]>(() => refine);

    const cgidRef = useMemo(
        () => result?.refinements?.find((r: any) => r.attributeId === 'cgid'),
        [result]
    );

    const getCategoryCount = useCallback(
        (id: string) => {
            return cgidRef?.values?.find((v: any) => v.value === id)?.hitCount;
        },
        [cgidRef]
    );

    const totalCount = useMemo(() => {
        if (!category) return undefined;
        const count = getCategoryCount(category.id);
        return count ?? result?.total;
    }, [category, getCategoryCount, result]);

    const searchCategories = useMemo(() => {
        if (!rootCategory?.categories || !cgidRef?.values?.length) return [];

        const allCategoriesMap = new Map<string, any>();

        (rootCategory.categories || []).forEach((c: any) => {
            allCategoriesMap.set(c.id, { ...c });
        });
        (subCategories || []).forEach((s: any) => {
            const existing = allCategoriesMap.get(s.id) || {};
            allCategoriesMap.set(s.id, { ...existing, ...s });
            (s.categories || []).forEach((child: any) => {
                const existingChild = allCategoriesMap.get(child.id) || {};
                allCategoriesMap.set(child.id, { parentCategoryId: s.id, ...existingChild, ...child });
            });
        });

        const rootId = rootCategory.id || 'root';
        const topLevelList = Array.from(allCategoriesMap.values()).filter(
            (c) => !c.parentCategoryId || c.parentCategoryId === rootId || c.parentCategoryId === 'root'
        );

        const buildFilteredTree = (cat: any): any => {
            const rawChildren: any[] =
                cat.categories && cat.categories.length > 0
                    ? cat.categories
                    : Array.from(allCategoriesMap.values()).filter((c) => c.parentCategoryId === cat.id);

            const seenChildIds = new Set<string>();
            const uniqueChildren = rawChildren.filter((c) => {
                if (c.id === cat.id || seenChildIds.has(c.id)) return false;
                seenChildIds.add(c.id);
                return true;
            });

            const filteredChildren = uniqueChildren
                .map((child) => buildFilteredTree(allCategoriesMap.get(child.id) || child))
                .filter(Boolean);

            const count = getCategoryCount(cat.id);
            const hasHits = typeof count === 'number' && count > 0;
            const hasChildHits = filteredChildren.length > 0;

            if (hasHits || hasChildHits) {
                return {
                    ...cat,
                    count,
                    categories: filteredChildren,
                };
            }
            return null;
        };

        return topLevelList.map(buildFilteredTree).filter(Boolean);
    }, [rootCategory, subCategories, cgidRef, getCategoryCount]);

    const hasSearchCategories = useMemo(() => {
        if (category) return true;
        return searchCategories.length > 0;
    }, [category, searchCategories]);

    const siblingCategories = useMemo(() => {
        if (!category || !rootCategory?.categories) return [];

        // If category is a top-level category under root
        if (!category.parentCategoryId || category.parentCategoryId === 'root') {
            return (rootCategory.categories || []).filter((c: any) => c.id !== category.id);
        }

        // If category is under a parent category
        const parentId = category.parentCategoryId;
        const findParent = (cats: any[]): any => {
            for (const cat of cats) {
                if (cat.id === parentId) return cat;
                if (cat.categories?.length) {
                    const found = findParent(cat.categories);
                    if (found) return found;
                }
            }
            return null;
        };

        const parent = findParent(rootCategory.categories);
        if (parent?.categories?.length) {
            return parent.categories.filter((c: any) => c.id !== category.id);
        }

        return [];
    }, [category, rootCategory]);

    const handleCategoryClick = useCallback(
        (categoryValue: string) => {
            const currentSearch = navigation.location ? navigation.location.search : location.search;
            const params = new URLSearchParams(currentSearch);
            const refines = params.getAll('refine');
            const cgidRefinement = `cgid=${categoryValue}`;

            const isSelected = refines.includes(cgidRefinement);

            let nextRefines: string[];
            if (isSelected) {
                nextRefines = refines.filter((r) => r !== cgidRefinement);
            } else {
                nextRefines = [...refines, cgidRefinement];
            }

            params.delete('refine');
            nextRefines.forEach((r) => params.append('refine', r));
            params.set('offset', '0');
            params.set('filters', 'open');

            void navigate({
                pathname: location.pathname,
                search: `?${params.toString()}`,
            });
        },
        [location, navigation.location, navigate]
    );

    const clearAllFilters = useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete('refine');
        params.set('offset', '0');
        params.set('filters', 'open');
        void navigate({
            pathname: location.pathname,
            search: `?${params.toString()}`,
        });
    }, [location, navigate]);

    const handleApplyAndClose = useCallback(() => {
        onToggle?.();
    }, [onToggle]);

    const handleCloseWithoutApply = useCallback(async () => {
        const params = new URLSearchParams(location.search);
        params.delete('refine');
        initialRefines.forEach((r) => params.append('refine', r));
        params.set('offset', '0');
        await navigate({ pathname: location.pathname, search: `?${params.toString()}` });
        onToggle?.();
    }, [initialRefines, location, navigate, onToggle]);

    const handleResetAndClose = useCallback(() => {
        clearAllFilters();
        onToggle?.();
    }, [clearAllFilters, onToggle]);

    // Check if any refinements are active
    const hasActiveFilters = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.getAll('refine').length > 0;
    }, [location.search]);

    /**
     * Optimistic refinements derived from the in-flight navigation target.
     *
     * When a user toggles a filter, `navigate()` updates the URL which triggers a loader call. While that navigation
     * is pending, `navigation.location` holds the target location, allowing us to read the intended refine params
     * immediately.
     *
     * Note: `navigation.location` is inherently tied to the pending state — it's only defined while
     * `navigation.state !== 'idle'`, and both reset in the same render cycle once the navigation completes. An
     * additional `isPending` guard is therefore not needed here.
     *
     * Minor trade-off: `useNavigation()` is global — it reflects any in-flight navigation, not just filter toggles.
     * In practice this is acceptable because if a different navigation starts, the entire page is about to change
     * anyway.
     */
    const effectiveRefines = navigation.location
        ? new URLSearchParams(navigation.location.search).getAll('refine')
        : refine;

    // Track which sections should be expanded (default open if has active filters)
    const hasActiveFilter = useCallback(
        (attributeId: string) => {
            return effectiveRefines.some((r) => r.startsWith(`${attributeId}=`));
        },
        [effectiveRefines]
    );

    // Category (`cgid`) selection is handled by QuickFilters, not the side filters panel.
    // Refinements are ordered: Price, Color, Size, then any others.
    const refinements = useMemo(() => {
        const filtered = (result?.refinements || []).filter((refinement) => refinement.attributeId !== 'cgid');

        const getRank = (id: string) => {
            const lowerId = id.toLowerCase();
            if (lowerId === 'price') return 1;
            if (lowerId.includes('color')) return 2;
            if (lowerId.includes('size')) return 3;
            return 4;
        };

        return [...filtered].sort((a, b) => getRank(a.attributeId) - getRank(b.attributeId));
    }, [result]);

    const toggleFilter = useCallback(
        (attributeId: string, value: string) => {
            const currentSearch = navigation.location ? navigation.location.search : location.search;
            const params = new URLSearchParams(currentSearch);
            const refines = params.getAll('refine');
            const refinePair = `${attributeId}=${value}`;

            let nextRefines: string[];
            if (refines.includes(refinePair)) {
                // Remove this refinement
                nextRefines = refines.filter((r) => r !== refinePair);
            } else {
                // Exclusive refinements - only one value can be selected at a time
                const exclusiveRefinements = [
                    'price',
                    // @sfdc-extension-line SFDC_EXT_BOPIS
                    'ilids',
                ];
                if (exclusiveRefinements.includes(attributeId)) {
                    // Remove all refinements for this attribute first
                    nextRefines = [...refines.filter((r) => !r.startsWith(`${attributeId}=`)), refinePair];
                } else {
                    nextRefines = [...refines, refinePair];
                }
            }

            // Rebuild search params with the new refines
            params.delete('refine');
            nextRefines.forEach((r) => params.append('refine', r));
            params.set('offset', '0');
            // Explicitly preserve open state when filtering
            params.set('filters', 'open');

            const nextSearch = `?${params.toString()}`;

            void navigate({
                pathname: location.pathname,
                search: nextSearch,
            });
        },
        [location, navigation.location, navigate]
    );

    // Check if a filter value is selected (uses optimistic state)
    const isFilterSelected = useCallback(
        (attributeId: string, value: string) => {
            return effectiveRefines.includes(`${attributeId}=${value}`);
        },
        [effectiveRefines]
    );

    // Render the appropriate filter component based on type
    const renderFilterValues = (
        refinement: ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] }
    ) => {
        const { attributeId, values } = refinement;
        const refinementProps: RefinementProps = {
            values,
            attributeId,
            isFilterSelected,
            toggleFilter,
        };
        switch (attributeId) {
            case 'c_refinementColor':
                return <RefineColor {...refinementProps} />;
            case 'c_size':
                return <RefineSize {...refinementProps} />;
            case 'price':
                return <RefinePrice {...refinementProps} result={result} />;
            default:
                return <RefineDefault {...refinementProps} />;
        }
    };

    const mobileSections = useMemo(() => {
        const sections: { id: string; label: string }[] = [];
        if (category || hasSearchCategories) {
            sections.push({ id: 'category', label: 'By Category' });
        }
        refinements.forEach((r) => {
            const rawLabel = r.label || r.attributeId;
            let formattedLabel = rawLabel;
            if (rawLabel.toLowerCase() === 'stock') {
                formattedLabel = 'Stock';
            } else if (!rawLabel.toLowerCase().startsWith('by ')) {
                formattedLabel = `By ${rawLabel}`;
            }
            sections.push({ id: r.attributeId, label: formattedLabel });
        });
        return sections;
    }, [category, refinements]);

    const renderMobileDetail = () => {
        if (selectedSection === 'category') {
            if (category) {
                return (
                    <div className="pt-1">
                        <div className="md:pb-5">
                            <FilterSection
                                label={`${category.name || category.id}${totalCount !== undefined ? ` (${totalCount})` : ''}`}
                                defaultOpen={true}
                                nested
                                labelClassName="text-[14px] md:text-[16px] font-semibold font-lato leading-6 text-black">
                                <CategoryChecklist
                                    category={category}
                                    result={result}
                                    activeRefinements={effectiveRefines}
                                    onCategoryClick={handleCategoryClick}
                                />
                            </FilterSection>
                        </div>

                        {siblingCategories.map((sibling: ShopperProducts.schemas['Category']) => {
                            const sibCount = getCategoryCount(sibling.id);
                            return (
                                <div key={sibling.id} className="border-t border-[#E5E5E5] pt-2 mt-2 last:border-b   md:pt-5 md:pb-2.5 md:mt-0">
                                    <FilterSection
                                        label={`${sibling.name || sibling.id}${sibCount !== undefined ? ` (${sibCount})` : ''}`}
                                        defaultOpen={false}
                                        nested
                                        labelClassName="text-[14px] md:text-[16px] font-semibold font-lato leading-6 text-black">
                                        <CategoryChecklist
                                            category={sibling}
                                            result={result}
                                            activeRefinements={effectiveRefines}
                                            onCategoryClick={handleCategoryClick}
                                        />
                                    </FilterSection>
                                </div>
                            );
                        })}
                    </div>
                );
            }
            if (hasSearchCategories && searchCategories.length > 0) {
                return (
                    <div className="pt-1">
                        {searchCategories.map((cat: any, index: number) => {
                            const catCount = cat.count ?? getCategoryCount(cat.id);
                            const hasChildren = Array.isArray(cat.categories) && cat.categories.length > 0;
                            return (
                                <div
                                    key={cat.id}
                                    className={index > 0 ? 'border-t border-[#E5E5E5] pt-2 mt-2 last:border-b md:pt-5 md:pb-2.5 md:mt-0' : 'md:pb-5'}>
                                    {hasChildren ? (
                                        <FilterSection
                                            label={`${cat.name || cat.id}${catCount !== undefined ? ` (${catCount})` : ''}`}
                                            defaultOpen={true}
                                            nested
                                            labelClassName="text-[14px] md:text-[16px] font-semibold font-lato leading-6 text-black">
                                            <CategoryChecklist
                                                category={cat}
                                                result={result}
                                                activeRefinements={effectiveRefines}
                                                onCategoryClick={handleCategoryClick}
                                            />
                                        </FilterSection>
                                    ) : (
                                        <div className="py-1">
                                            <label className="cat flex flex-1 cursor-pointer select-none items-center">
                                                <Checkbox
                                                    checked={effectiveRefines.includes(`cgid=${cat.id}`)}
                                                    onCheckedChange={() => handleCategoryClick(cat.id)}
                                                    className="size-4 shrink-0 rounded-[4px] border-[#8C8C8C] text-primary"
                                                />
                                                <span
                                                    className={`ml-3 text-[12px] md:text-[14px] ${effectiveRefines.includes(`cgid=${cat.id}`)
                                                        ? 'font-semibold text-primary'
                                                        : 'font-normal text-foreground'
                                                        }`}>
                                                    {cat.name || cat.id}
                                                    {catCount !== undefined ? ` (${catCount})` : ''}
                                                </span>
                                            </label>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            }
            return null;
        }

        const refinement = refinements.find((r) => r.attributeId === selectedSection);
        if (!refinement || !Array.isArray(refinement.values) || !refinement.values.length) {
            return null;
        }
        return renderFilterValues(
            refinement as ShopperSearch.schemas['ProductSearchRefinement'] & { values: FilterValue[] }
        );
    };

    // No refinements available
    if (refinements.length === 0) {
        return (
            <div className="border rounded-none p-4">
                <p className="text-muted-foreground text-sm">{t('categoryRefinements:noFilterOptionsAvailable')}</p>
            </div>
        );
    }

    return (
        <UITarget targetId="sfcc.plp.search.filters">
            <div
                className={`relative flex h-fit flex-col ${isPending ? 'pointer-events-none opacity-50 transition-opacity' : ''}`}>
                {/* Panel header: Filters title + Clear All (was .filter-panel-header / .filter-panel-toggle / .filter-panel-title / .filter-panel-clear) */}
                <div className="filter-panel-header flex items-center justify-between border-b border-[#E0E0E0] px-4 py-3">
                    <div className="flex items-center gap-2">
                        <FiltersIcon className="size-5 text-black shrink-0" />
                        <span className="filter-panel-title text-[14px] font-semibold text-black tracking-[0.01em] leading-none">
                            Filters
                        </span>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Clear All */}
                        <button
                            type="button"
                            className="filter-panel-clear cursor-pointer"
                            onClick={clearAllFilters}
                            aria-label="Clear all filters">
                            Clear All
                        </button>

                        <button type="button" onClick={() => { void handleCloseWithoutApply(); }} className="flex size-8 items-center justify-center md:hidden" aria-label="Close filters">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 32 32" fill="none">
                                <path d="M25 7L7 25" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M25 25L7 7" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="min-h-0 overflow-y-auto px-4 md:py-[24px]">
                    <div className={`hidden md:block ${FILTER_PANEL_SKIN}`}>
                        {category ? (
                            <FilterSection label="Category" defaultOpen={true}>
                                <div>
                                    <div className="md:pb-2.5">
                                        <FilterSection
                                            label={`${category.name || category.id}${totalCount !== undefined ? ` (${totalCount})` : ''}`}
                                            defaultOpen={true}
                                            nested
                                            labelClassName="text-[14px] md:text-[16px] font-semibold font-lato leading-6 text-black">
                                            <CategoryChecklist
                                                category={category}
                                                result={result}
                                                activeRefinements={effectiveRefines}
                                                onCategoryClick={handleCategoryClick}
                                            />
                                        </FilterSection>
                                    </div>

                                    {siblingCategories.map((sibling: ShopperProducts.schemas['Category']) => {
                                        const sibCount = getCategoryCount(sibling.id);
                                        return (
                                            <div key={sibling.id} className="border-t border-[#E5E5E5] pt-2 mt-2 last:border-b  md:pt-5 md:pb-2.5 md:mt-0">
                                                <FilterSection
                                                    label={`${sibling.name || sibling.id}${sibCount !== undefined ? ` (${sibCount})` : ''}`}
                                                    defaultOpen={false}
                                                    nested
                                                    labelClassName="text-[14px] md:text-[16px] font-semibold font-lato leading-6 text-black">
                                                    <CategoryChecklist
                                                        category={sibling}
                                                        result={result}
                                                        activeRefinements={effectiveRefines}
                                                        onCategoryClick={handleCategoryClick}
                                                    />
                                                </FilterSection>
                                            </div>
                                        );
                                    })}
                                </div>
                            </FilterSection>
                        ) : (
                            hasSearchCategories && searchCategories.length > 0 && (
                                <FilterSection label="Category" defaultOpen={true}>
                                    <div>
                                        {searchCategories.map((cat: any, index: number) => {
                                            const catCount = cat.count ?? getCategoryCount(cat.id);
                                            const hasChildren = Array.isArray(cat.categories) && cat.categories.length > 0;
                                            return (
                                                <div
                                                    key={cat.id}
                                                    className={index > 0 ? 'border-t border-[#E5E5E5] pt-2 mt-2 md:pt-5 md:pb-2.5 md:mt-0' : 'md:pb-2.5'}>
                                                    {hasChildren ? (
                                                        <FilterSection
                                                            label={`${cat.name || cat.id}${catCount !== undefined ? ` (${catCount})` : ''}`}
                                                            defaultOpen={true}
                                                            nested
                                                            labelClassName="text-[14px] md:text-[16px] font-semibold font-lato leading-6 text-black">
                                                            <CategoryChecklist
                                                                category={cat}
                                                                result={result}
                                                                activeRefinements={effectiveRefines}
                                                                onCategoryClick={handleCategoryClick}
                                                            />
                                                        </FilterSection>
                                                    ) : (
                                                        <div className="py-1">
                                                            <label className="cat flex flex-1 cursor-pointer select-none items-center">
                                                                <Checkbox
                                                                    checked={effectiveRefines.includes(`cgid=${cat.id}`)}
                                                                    onCheckedChange={() => handleCategoryClick(cat.id)}
                                                                    className="size-4 shrink-0 rounded-[4px] border-[#8C8C8C] text-primary"
                                                                />
                                                                <span
                                                                    className={`ml-3 text-[12px] md:text-[14px] ${effectiveRefines.includes(`cgid=${cat.id}`)
                                                                        ? 'font-semibold text-primary'
                                                                        : 'font-normal text-foreground'
                                                                        }`}>
                                                                    {cat.name || cat.id}
                                                                    {catCount !== undefined ? ` (${catCount})` : ''}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </FilterSection>
                            )
                        )}


                        {refinements.map((refinement) => {
                            const { values, attributeId, label } = refinement;
                            if (!Array.isArray(values) || !values.length) {
                                return null;
                            }

                            return (
                                <FilterSection
                                    key={attributeId}
                                    label={label || attributeId}
                                    defaultOpen={true}>
                                    {renderFilterValues(
                                        refinement as ShopperSearch.schemas['ProductSearchRefinement'] & {
                                            values: FilterValue[];
                                        }
                                    )}
                                </FilterSection>
                            );
                        })}
                    </div>

                    {/* ─── Mobile: left tabs / right detail panel ─── */}
                    <div className="flex h-full md:hidden">
                        <div className="w-[34%] shrink-0 overflow-y-auto border-r border-[#E5E5E5] pb-24">
                            {mobileSections.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => setSelectedSection(s.id)}
                                    className={`block w-full border-b border-[#E5E5E5] bg-transparent py-3.5 pr-2 text-left text-[14px] ${selectedSection === s.id ? 'font-bold text-black' : 'font-normal text-[#8C8C8C]'
                                        }`}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        <div className={`flex-1 overflow-y-auto p-4 max-h-[65vh] overflow-y-scroll pt-0 pb-24 ${FILTER_PANEL_SKIN}`}>{renderMobileDetail()}</div>
                    </div>
                </div>

                {/* Mobile Reset / Apply */}
                <div className="absolute bottom-0 left-0 right-0 z-10 flex h-[72px] shrink-0 gap-3 border-t border-[#E5E5E5] bg-white p-3 md:hidden">
                    <button type="button" onClick={handleResetAndClose} className="h-11 flex-1 rounded-full border border-black bg-white text-sm font-medium">
                        Reset
                    </button>
                    <button type="button" onClick={handleApplyAndClose} className="h-11 flex-1 rounded-full bg-black text-sm font-medium text-white">
                        Apply
                    </button>
                </div>
            </div>
        </UITarget>
    );
}

/**
 * Individual filter section with collapsible behavior.
 * (was .filter-section: margin-bottom 10px; .filter-section-trigger: flex row, px-4 pt-3 pb-1,
 * hover bg black/3%; .filter-section-trigger span: text-sm font-normal)
 * When `nested` is true, renders without the outer spacing so it can sit inside another
 * FilterSection (e.g. the category name inside the outer "Category" section) — this variant
 * uses `.category-filter-item`-style typography via `labelClassName`.
 */
function FilterSection({
    label,
    defaultOpen = false,
    nested = false,
    labelClassName = 'text-sm font-normal leading-5 text-black font-lato',
    children,
}: {
    label: string;
    defaultOpen?: boolean;
    nested?: boolean;
    labelClassName?: string;
    children: ReactElement;
}): ReactElement {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <section className={nested ? '' : 'mb-2.5'}>
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
                <Typography variant="small" as="h3" className="leading-normal">
                    <CollapsibleTrigger
                        className={`flex w-full items-center border-none bg-transparent text-left ${nested ? 'justify-start gap-2' : 'justify-between p-0'
                            }`}>
                        {nested && (isOpen ? <ChevronUp className="size-4 shrink-0" /> : <ChevronDown className="size-4 shrink-0" />)}
                        <Typography variant="small" as="span" className={labelClassName}>
                            {label}
                        </Typography>
                        {!nested && (isOpen ? <Minus className="size-4" /> : <Plus className="size-4" />)}
                    </CollapsibleTrigger>
                </Typography>
                <CollapsibleContent className={nested ? 'pb-4' : ' pb-9 pt-2'}>{children}</CollapsibleContent>
            </Collapsible>
        </section>
    );
}

type CategoryNode = {
    id: string;
    name: string;
    count?: number;
    categories: any[];
};

function CategoryTreeItem({
    node,
    depth,
    activeRefinements,
    onCategoryClick,
    getCategoryData,
}: {
    node: CategoryNode;
    depth: number;
    activeRefinements: string[];
    onCategoryClick: (id: string) => void;
    getCategoryData: (cat: any) => CategoryNode;
}) {
    const [isOpen, setIsOpen] = useState(depth === 0);
    const hasChildren = node.categories && node.categories.length > 0;
    const isActive = activeRefinements.includes(`cgid=${node.id}`);

    const children = useMemo(() => {
        if (!hasChildren) return [];
        return [...node.categories].map(getCategoryData).sort((a, b) => a.id.localeCompare(b.id));
    }, [node.categories, getCategoryData, hasChildren]);

    return (
        <div className={depth > 0 ? 'pl-5' : ''}>
            <div className="flex items-center justify-between">
                <label className="cat flex flex-1 cursor-pointer select-none items-center">
                    <Checkbox
                        checked={isActive}
                        onCheckedChange={() => onCategoryClick(node.id)}
                        className="size-4 shrink-0 rounded-[4px] border-[#8C8C8C] text-primary"
                    />
                    <span
                        className={`ml-3 text-[12px] md:text-[14px] ${isActive ? 'font-semibold text-primary' : 'font-normal text-foreground'
                            }`}>
                        {node.name}
                        {node.count !== undefined ? ` (${node.count})` : ''}
                    </span>
                </label>
                {hasChildren && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsOpen(!isOpen);
                        }}
                        className="p-1 text-muted-foreground hover:text-foreground">
                        {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                    </button>
                )}
            </div>
            {hasChildren && isOpen && (
                <div className="filter-category-item">
                    {children.map((child) => (
                        <CategoryTreeItem
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                            activeRefinements={activeRefinements}
                            onCategoryClick={onCategoryClick}
                            getCategoryData={getCategoryData}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function CategoryChecklist({
    category,
    result,
    activeRefinements,
    onCategoryClick,
}: {
    category: ShopperProducts.schemas['Category'];
    result: any;
    activeRefinements: string[];
    onCategoryClick: (id: string) => void;
}) {
    const cgidRef = result?.refinements?.find((r: any) => r.attributeId === 'cgid');

    const getCategoryData = useCallback(
        (cat: any): CategoryNode => {
            const count = cat.count ?? cgidRef?.values?.find((v: any) => v.value === cat.id)?.hitCount;
            return {
                id: cat.id,
                name: cat.name || cat.id,
                count,
                categories: cat.categories || [],
            };
        },
        [cgidRef]
    );

    // Sort subcategories alphabetically by id
    const subCategories = useMemo(() => {
        const list = (category.categories || []).map(getCategoryData);
        return [...list].sort((a, b) => a.id.localeCompare(b.id));
    }, [category.categories, getCategoryData]);

    return (
        <div className="md:mt-2 space-y-3 pl-1">
            {subCategories.map((subCat) => (
                <CategoryTreeItem
                    key={subCat.id}
                    node={subCat}
                    depth={0}
                    activeRefinements={activeRefinements}
                    onCategoryClick={onCategoryClick}
                    getCategoryData={getCategoryData}
                />
            ))}
        </div>
    );
}
